/* eslint-disable padding-line-between-statements */
/* eslint-disable @typescript-eslint/no-extra-semi */
import type { AnalyticsInstance } from 'analytics'
import { AnalyticsPayload, SuperProperties } from '../../../models/Analytics'
import { sendAnonUserIdMessage } from '../../../sdk/utils/sdk-messenger'
import { getClassySessionId } from 'modules/utils/getClassySessionId'

export type HeapCookie = {
  userId: string
  pageviewId: string
  sessionId: string
}

type PluginData = {
  config: SuperProperties
  payload: AnalyticsPayload
  instance: AnalyticsInstance
}

// Extend Window to include heapReadyCb
declare global {
  interface Window {
    heapReadyCb?: Array<{ name: string; fn: () => void }>
  }
}

/**
 * Event queue for holding events until Heap is loaded
 */
export const eventQueue: Array<{ method: keyof HeapInterface; args: unknown[] }> = []

// Define the Heap interface to ensure type safety on `window.heap`
interface HeapInterface {
  identify: (
    userId: string,
    traits?: any,
    options?: any,
    callback?: (...params: any[]) => any,
  ) => Promise<any>
  load: (appId: string, config?: Record<string, unknown>) => void
  appid: string
  config: Record<string, unknown>
  push: (args: unknown[]) => unknown
  track: (event: string, args: object) => unknown
  clearEventProperties: () => unknown
  addUserProperties: (properties: Record<string, unknown>) => void
  addEventProperties: (properties: Record<string, unknown>) => void
}

// Helper function to retrieve the Heap user ID from local storage
export const getHeapUserId = (): string | undefined => {
  const heapData = JSON.parse(
    localStorage.getItem(`_hp5_meta.${process.env.NEXT_PUBLIC_HEAP_APP_ID}`) || '{}',
  )

  return heapData?.value?.userId
}

/**
 * Custom heap analytics plugin:
 * https://getanalytics.io/plugins/writing-plugins/
 * https://developers.heap.io/docs/web
 */
const heap = (heapSuperProperties: SuperProperties) => ({
  name: 'heap',
  config: {
    ...heapSuperProperties,
    campaignType: 'studio',
  },

  initialize(): void {
    const heapAppId = process.env.NEXT_PUBLIC_HEAP_APP_ID

    if (!heapAppId) {
      console.error('Heap App ID is missing.')
      return
    }

    const heapConfigScript = document.createElement('script')

    heapConfigScript.type = 'text/javascript'

    heapConfigScript.text = `
      window.heapReadyCb = window.heapReadyCb || [];
      window.heap = window.heap || [];

      heap.load = function (e, t) {
        window.heap.envId = e;
        window.heap.clientConfig = t = t || {};
        window.heap.clientConfig.shouldFetchServerConfig = false;
        var a = document.createElement("script");
        a.type = "text/javascript";
        a.async = true;
        a.src = "https://cdn.us.heap-api.com/config/" + e + "/heap_config.js";
        var r = document.getElementsByTagName("script")[0];
        r.parentNode.insertBefore(a, r);
        var n = ["init", "startTracking", "stopTracking", "track", "resetIdentity", "identify", "getSessionId", "getUserId", "getIdentity", "addUserProperties", "addEventProperties", "removeEventProperty", "clearEventProperties", "addAccountProperties", "addAdapter", "addTransformer", "addTransformerFn", "onReady", "addPageviewProperties", "removePageviewProperty", "clearPageviewProperties", "trackPageview"];
        var i = function (e) { return function () { var t = Array.prototype.slice.call(arguments, 0); window.heapReadyCb.push({ name: e, fn: function () { heap[e] && heap[e].apply(heap, t); } }) } };
        for (var p = 0; p < n.length; p++) heap[n[p]] = i(n[p]);
      };

      heap.load("${heapAppId}", {
        metadataStorage: "localstorage",
        eventPropertiesStorage: "localstorage",
      });
    `

    document.head.appendChild(heapConfigScript)

    // Queue super properties with addEventProperties on Heap initialization
    window.heapReadyCb?.push({
      name: 'onReady',
      fn: () => {
        // Add super properties and classy-session-id to Heap event properties
        const sessionId = getClassySessionId()

        const eventProperties = sessionId
          ? { ...heapSuperProperties, 'classy-session-id': sessionId }
          : heapSuperProperties

        /**
         * Once initalized add any relevent event properties
         */
        window.heap.addEventProperties(eventProperties)

        /**
         * Now process any queue'd events
         */
        processEventQueue()

        /**
         * Once Heap has set a userId send it back to the SDK
         * so that it can be stored in a firsty party cooking for future usage.
         */
        const heapUserId = getHeapUserId()

        if (heapUserId) {
          sendAnonUserIdMessage(heapUserId)

          console.log('[Heap] Sent anonymous user ID to SDK:', heapUserId)
        }
      },
    })
  },

  page({ payload, config: superProperties }: PluginData): void {
    queueOrExecute('track', ['Page View', { ...superProperties, ...payload.properties }])
  },

  track({ payload, config: superProperties }: PluginData): void {
    const { event, properties } = payload

    if (event) {
      queueOrExecute('track', [event, { ...superProperties, ...properties }])
    }
  },

  identify({ payload }: { payload: { userId: string } }): void {
    const { userId } = payload
    queueOrExecute('identify', [userId])
  },
  loaded(): boolean {
    return !!window.heap
  },
  methods: {
    addUserProperties(properties: Record<string, unknown>): void {
      queueOrExecute('addUserProperties', [properties])
    },
  },
})

// Helper function to queue events when Heap is not loaded or to execute directly if Heap is loaded
const queueOrExecute = (method: keyof HeapInterface, args: unknown[]) => {
  if (window.heap?.[method]) {
    ;(window.heap[method] as (...args: unknown[]) => void)(...args)
  } else {
    eventQueue.push({ method, args })
  }
}

// Process queued events once Heap is loaded
const processEventQueue = () => {
  while (eventQueue.length > 0) {
    const event = eventQueue.shift()

    if (event) {
      const { method, args } = event
      if (window.heap[method]) {
        ;(window.heap[method] as (...args: unknown[]) => void)(...args)

        console.log(`[Heap] Processed queued event: ${method}`, args)
      }
    }
  }
}

export default heap
